// SCR-STAFF-994 (تنظیمات) — انواع و دسته‌های تیکت و ساعات کاری SLA (US-CHN-040؛ AC-CHN-040.1؛ BR-CHN-041؛ AP-CHN-02): هر دسته اولویت پیش‌فرض، SLA پاسخ اول و حل (ساعت
// کاری؛ یک روز کاری = ۸ ساعت — C-AGENT-1504) و تیم یا مسئول پیش‌فرض دارد؛ دسته بالینی (فقط شکایت) حساسیت Clinical می‌گیرد. ساعات کاری هفتگی مرکز با تعطیلات تقویم
// شعبه ترکیب می‌شود (`chn.ticket.manage`).
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type ChnBusinessHoursWindow,
  type ChnTicketCategoryRequest,
  type ChnTicketCategoryView,
  type ChnTicketPriority,
} from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, faDigits, FormDialog, ListPage, LoadingState, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { WriteNotice } from '../settings/common';
import { categoryLabel, SCR_STAFF_994, TICKET_PRIORITIES, TICKET_TYPES, useTicketCategories } from './ticketing';

const TEAMS = ['Manager', 'CallCenterAgent', 'Receptionist'] as const;
/** ترتیب روزهای هفته ایرانی (شنبه نخست) با شماره DayOfWeek سرور (۰ یکشنبه). */
const WEEK: readonly number[] = [6, 0, 1, 2, 3, 4, 5];

function hours(minutes: number | string): string {
  const value = Number(minutes) / 60;
  return faDigits(Number.isInteger(value) ? String(value) : value.toFixed(1));
}

export function TicketSettingsPage() {
  const { t } = useTranslation('chn');
  const write = useWriteAccess('chn.ticket.manage');
  const [tab, setTab] = useState<'categories' | 'hours'>('categories');
  return (
    <ListPage
      screenId={SCR_STAFF_994}
      titleKey="chn:ticket_settings.title"
      subtitleKey="chn:ticket_settings.subtitle"
      filters={
        <Stack spacing={1}>
          <WriteNotice write={write} />
          <Tabs value={tab} onChange={(_event, next: 'categories' | 'hours') => setTab(next)} aria-label={t('chn:ticket_settings.title')}>
            <Tab value="categories" label={t('chn:ticket_settings.categories')} data-testid="chn-settings-tab-categories" />
            <Tab value="hours" label={t('chn:ticket_settings.hours')} data-testid="chn-settings-tab-hours" />
          </Tabs>
        </Stack>
      }
    >
      {tab === 'categories' ? <CategoriesTab canWrite={write.canWrite} /> : <HoursTab canWrite={write.canWrite} />}
    </ListPage>
  );
}

function CategoriesTab({ canWrite }: { canWrite: boolean }) {
  const { t } = useTranslation('chn');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const categories = useTicketCategories(true);
  const [editing, setEditing] = useState<ChnTicketCategoryView | 'new' | null>(null);
  const toggle = useMutation({
    mutationFn: (row: ChnTicketCategoryView) =>
      unwrap(
        row.isActive
          ? tenantApi.POST('/api/v1/channels/ticket-categories/{id}:deactivate', { params: { path: { id: row.id } } })
          : tenantApi.POST('/api/v1/channels/ticket-categories/{id}:activate', { params: { path: { id: row.id } } }),
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['chn', session?.tenantId, 'ticket-categories'] }),
  });

  const columns: DataTableColumn<ChnTicketCategoryView>[] = [
    {
      id: 'name',
      headerKey: 'chn:ticket_settings.columns.name',
      cell: (row) => (
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
          <span>{categoryLabel(t, row.code, row.name)}</span>
          {row.isClinical ? <Chip size="small" color="error" variant="outlined" label={t('chn:tickets.clinical')} /> : null}
          {!row.isActive ? <Chip size="small" variant="outlined" label={t('chn:ticket_settings.inactive')} /> : null}
        </Stack>
      ),
    },
    { id: 'type', headerKey: 'chn:ticket_settings.columns.type', cell: (row) => t(`chn:tickets.type.${row.type}`) },
    { id: 'priority', headerKey: 'chn:ticket_settings.columns.priority', cell: (row) => t(`chn:tickets.priority.${row.priority}`) },
    { id: 'first', headerKey: 'chn:ticket_settings.columns.first_response', cell: (row) => <span data-testid={`chn-category-first-${row.code}`}>{t('chn:ticket_settings.hours_value', { value: hours(row.firstResponseMinutes) })}</span> },
    { id: 'resolution', headerKey: 'chn:ticket_settings.columns.resolution', cell: (row) => t('chn:ticket_settings.hours_value', { value: hours(row.resolutionMinutes) }) },
    { id: 'owner', headerKey: 'chn:ticket_settings.columns.owner', cell: (row) => row.defaultAssigneeName ?? (row.defaultTeam ? t(`iam:role.${row.defaultTeam}`, { defaultValue: row.defaultTeam }) : '—') },
    { id: 'portal', headerKey: 'chn:ticket_settings.columns.portal', cell: (row) => (row.portalVisible ? t('chn:ticket_settings.yes') : '—') },
    {
      id: 'actions',
      headerKey: 'chn:ticket_settings.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" disabled={!canWrite} onClick={() => setEditing(row)} data-testid={`chn-category-edit-${row.code}`}>
            {t('chn:ticket_settings.edit')}
          </Button>
          <Button size="small" color={row.isActive ? 'error' : 'primary'} disabled={!canWrite || toggle.isPending} onClick={() => toggle.mutate(row)}>
            {t(row.isActive ? 'chn:ticket_settings.deactivate' : 'chn:ticket_settings.activate')}
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Stack spacing={1}>
      <Box>
        <Button variant="contained" startIcon={<AddIcon />} disabled={!canWrite} onClick={() => setEditing('new')} data-testid="chn-category-add">
          {t('chn:ticket_settings.add')}
        </Button>
      </Box>
      {toggle.error ? <ErrorState error={toggle.error} compact /> : null}
      <DataTable
        captionKey="chn:ticket_settings.categories"
        columns={columns}
        rows={categories.data?.items}
        getRowId={(row) => row.id}
        isLoading={categories.isPending}
        error={categories.error}
        onRetry={() => void categories.refetch()}
        emptyTitleKey="chn:ticket_settings.empty"
      />
      {editing ? <CategoryDialog category={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </Stack>
  );
}

function CategoryDialog({ category, onClose }: { category: ChnTicketCategoryView | null; onClose: () => void }) {
  const { t } = useTranslation('chn');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    code: category?.code ?? '',
    type: category?.type ?? 'Internal',
    name: category?.name ?? '',
    priority: (category?.priority ?? 'Normal') as ChnTicketPriority,
    firstHours: category ? String(Number(category.firstResponseMinutes) / 60) : '8',
    resolutionHours: category ? String(Number(category.resolutionMinutes) / 60) : '40',
    team: category?.defaultTeam ?? '',
    isClinical: category?.isClinical ?? false,
    portalVisible: category?.portalVisible ?? false,
    sortOrder: String(category?.sortOrder ?? 100),
  });
  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm((current) => ({ ...current, [key]: value }));
  const save = useMutation({
    mutationFn: () => {
      const body: ChnTicketCategoryRequest = {
        code: category ? null : form.code.trim(),
        type: category ? null : form.type,
        name: form.name.trim() || null,
        priority: form.priority,
        firstResponseMinutes: Math.round(Number(form.firstHours) * 60),
        resolutionMinutes: Math.round(Number(form.resolutionHours) * 60),
        defaultTeam: form.team || null,
        defaultAssigneeMembershipId: category?.defaultAssigneeMembershipId ?? null,
        isClinical: form.isClinical,
        portalVisible: form.portalVisible,
        sortOrder: Number(form.sortOrder) || 0,
      };
      return unwrap(
        category
          ? tenantApi.PUT('/api/v1/channels/ticket-categories/{id}', { params: { path: { id: category.id } }, body })
          : tenantApi.POST('/api/v1/channels/ticket-categories', { body }),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['chn', session?.tenantId, 'ticket-categories'] });
      onClose();
    },
  });
  const patientFacing = form.type !== 'Internal';
  return (
    <FormDialog
      open
      titleKey={category ? 'chn:ticket_settings.edit_title' : 'chn:ticket_settings.add'}
      screenId={SCR_STAFF_994}
      onSubmit={() => save.mutate()}
      onClose={onClose}
      isDirty
      isSubmitting={save.isPending}
      submitError={save.error}
      maxWidth="md"
    >
      {!category ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField required label={t('chn:ticket_settings.fields.code')} value={form.code} onChange={(event) => set('code', event.target.value)} helperText={t('chn:ticket_settings.fields.code_hint')} slotProps={{ htmlInput: { dir: 'ltr', maxLength: 40, 'data-testid': 'chn-category-code' } }} fullWidth />
          <TextField select required label={t('chn:ticket_settings.fields.type')} value={form.type} onChange={(event) => set('type', event.target.value)} fullWidth>
            {TICKET_TYPES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`chn:tickets.type.${value}`)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      ) : null}
      <TextField
        label={t('chn:ticket_settings.fields.name')}
        required={!category?.isSystem}
        value={form.name}
        placeholder={category?.isSystem ? categoryLabel(t, category.code, null) : undefined}
        onChange={(event) => set('name', event.target.value)}
        slotProps={{ htmlInput: { maxLength: 100, 'data-testid': 'chn-category-name' } }}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField select label={t('chn:ticket_settings.fields.priority')} value={form.priority} onChange={(event) => set('priority', event.target.value as ChnTicketPriority)} fullWidth>
          {TICKET_PRIORITIES.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`chn:tickets.priority.${value}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField select label={t('chn:ticket_settings.fields.team')} value={form.team} onChange={(event) => set('team', event.target.value)} fullWidth>
          <MenuItem value="">{t('chn:ticket_settings.fields.no_team')}</MenuItem>
          {TEAMS.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`iam:role.${value}`, { defaultValue: value })}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField type="number" required label={t('chn:ticket_settings.fields.first_response_hours')} value={form.firstHours} onChange={(event) => set('firstHours', event.target.value)} slotProps={{ htmlInput: { min: 0.25, step: 0.25, 'data-testid': 'chn-category-first' } }} fullWidth />
        <TextField type="number" required label={t('chn:ticket_settings.fields.resolution_hours')} value={form.resolutionHours} onChange={(event) => set('resolutionHours', event.target.value)} helperText={t('chn:ticket_settings.fields.business_day_hint')} slotProps={{ htmlInput: { min: 0.25, step: 0.25, 'data-testid': 'chn-category-resolution' } }} fullWidth />
      </Stack>
      <Stack direction="row" spacing={2} flexWrap="wrap">
        <FormControlLabel disabled={form.type !== 'PatientComplaint'} control={<Checkbox checked={form.isClinical} onChange={(event) => set('isClinical', event.target.checked)} />} label={t('chn:ticket_settings.fields.clinical')} />
        <FormControlLabel disabled={!patientFacing} control={<Checkbox checked={form.portalVisible} onChange={(event) => set('portalVisible', event.target.checked)} />} label={t('chn:ticket_settings.fields.portal')} />
      </Stack>
      <TextField type="number" label={t('chn:ticket_settings.fields.sort')} value={form.sortOrder} onChange={(event) => set('sortOrder', event.target.value)} sx={{ maxWidth: 160 }} />
    </FormDialog>
  );
}

function HoursTab({ canWrite }: { canWrite: boolean }) {
  const { t } = useTranslation('chn');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const key = ['chn', session?.tenantId, 'ticket-settings'];
  const settings = useQuery({ queryKey: key, queryFn: () => unwrap(tenantApi.GET('/api/v1/channels/ticket-settings')) });
  const [windows, setWindows] = useState<ChnBusinessHoursWindow[]>([]);
  useEffect(() => {
    if (settings.data) {
      setWindows(settings.data.hours);
    }
  }, [settings.data]);
  const save = useMutation({
    mutationFn: () => unwrap(tenantApi.PUT('/api/v1/channels/ticket-settings', { body: { hours: windows } })),
    onSuccess: (next) => queryClient.setQueryData(key, next),
  });
  if (settings.isPending) {
    return <LoadingState rows={3} />;
  }
  if (settings.error) {
    return <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />;
  }
  const update = (index: number, patch: Partial<ChnBusinessHoursWindow>) => setWindows((current) => current.map((item, position) => (position === index ? { ...item, ...patch } : item)));
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('chn:ticket_settings.hours_hint')}
          </Typography>
          {WEEK.map((day) => (
            <Stack key={day} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} role="group" aria-label={t(`chn:ticket_settings.days.${day}`)} data-testid={`chn-hours-day-${day}`}>
              <Typography component="span" variant="subtitle2" sx={{ minWidth: 96 }}>
                {t(`chn:ticket_settings.days.${day}`)}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {windows.map((item, index) =>
                  Number(item.day) === day ? (
                    <Stack key={index} direction="row" spacing={0.5} alignItems="center">
                      <TextField size="small" type="time" label={t('chn:ticket_settings.from')} value={item.from} onChange={(event) => update(index, { from: event.target.value })} disabled={!canWrite} slotProps={{ htmlInput: { dir: 'ltr' } }} />
                      <TextField size="small" type="time" label={t('chn:ticket_settings.to')} value={item.to} onChange={(event) => update(index, { to: event.target.value })} disabled={!canWrite} slotProps={{ htmlInput: { dir: 'ltr' } }} />
                      <IconButton aria-label={t('chn:ticket_settings.remove_window')} disabled={!canWrite} onClick={() => setWindows((current) => current.filter((_item, position) => position !== index))}>
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Stack>
                  ) : null,
                )}
                {!windows.some((item) => Number(item.day) === day) ? (
                  <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    {t('chn:ticket_settings.closed_day')}
                  </Typography>
                ) : null}
                <Button size="small" disabled={!canWrite} onClick={() => setWindows((current) => [...current, { day, from: '08:00', to: '16:00' }])}>
                  {t('chn:ticket_settings.add_window')}
                </Button>
              </Stack>
            </Stack>
          ))}
          {save.error ? <ErrorState error={save.error} compact /> : null}
          {save.isSuccess ? (
            <Typography variant="body2" color="success.main" role="status">
              {t('chn:ticket_settings.saved')}
            </Typography>
          ) : null}
          <Box>
            <Button variant="contained" disabled={!canWrite || save.isPending} onClick={() => save.mutate()} data-testid="chn-hours-save">
              {t('chn:ticket_settings.save_hours')}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

